import { useState, useEffect } from 'react';
import { NavLink, useParams } from "react-router-dom";
import axios from 'axios'
import Swal from 'sweetalert2'

import DescriptionListItem from '../components/DescriptionListItem'
import Loading from '../components/Loading';
import ResourceNotFound from '../components/ResourceNotFound';
import PageHeader from '../components/PageHeader';
import Deleting from '../components/Deleting';

class toDisplay {
  constructor(oldContact, contactIfEmpty){ 
    const contact_with_italic_map = new Map();
    if (oldContact.length == 0) {
      contact_with_italic_map.set('contact',contactIfEmpty);
      contact_with_italic_map.set('italic',true);
    } 
      else {
        contact_with_italic_map.set('contact',oldContact);
        contact_with_italic_map.set('italic',false);
      }
    return contact_with_italic_map;
  }
}

const ServiceRead = () => {
  const [serviceDetail, setServiceDetail] = useState(null)
  const [serviceNotFound, setServiceNotFound] = useState(null)
  const [serviceDeleting, setServiceDeleting] = useState(null)

  // Get the "tag" parameter from the URL
  const { tag: serviceTag } = useParams()

  // When the component renders for the first time, load the data from API
  useEffect(() => {
    const callAPI = () => {
      axios({
        method: 'GET',
        url: `${process.env.REACT_APP_API_BASE_URL}/services/${serviceTag}`,
        headers: { 'content-type': 'application/json' },
      }).then(response => {
        setServiceDetail(response.data)
      }).catch(error => {
        console.error('error', error)
        if(error.response && error.response.status === 404) {
          setServiceNotFound(error.response.data)
        }
      });
    }
    callAPI()
  }, [serviceTag])

  if (serviceNotFound) {
    return <ResourceNotFound pageTitle="Service view" message={serviceNotFound.Message}/>
  }

  // React will try and render the component before the useEffect executes, so we need to handle
  // what to show when we don't yet have data to show
  if (serviceDetail === null) {
    return <Loading pageTitle="Service view" />
  }

  if (serviceDeleting) {
    const { status } = serviceDeleting
    return <Deleting pageTitle={serviceDetail.Service} inProgress={status === 'DELETING'} />
  }

  const accountIcon = (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
  </svg>)

  const poundIcon = <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9a2 2 0 10-4 0v5a2 2 0 01-2 2h6m-6-4h4m8 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>

  const securityIcon = <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>

  const serviceIcon = <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>

  const accounts = serviceDetail.Accounts.map(account => {
    return {
      label: account,
      url: `/accounts/${account}`
    }
  })

  const convertStringToLabelObjForTable = item => {
    return { label: item }
  }

  const billingContacts = serviceDetail.BillingContacts.map(convertStringToLabelObjForTable)
  const serviceContacts = serviceDetail.ServiceContacts.map(convertStringToLabelObjForTable)
  const securityContacts = serviceDetail.SecurityContacts.map(convertStringToLabelObjForTable)

  // owner field
  const projectOwners = serviceDetail.ProjectOwners.map(convertStringToLabelObjForTable)

  const billingContactstoDisplay = new toDisplay(billingContacts,projectOwners);
  const serviceContactstoDisplay = new toDisplay(serviceContacts,projectOwners);
  const securityContactstoDisplay = new toDisplay(securityContacts,projectOwners);

  const handleDeleteService = () => {
    setServiceDeleting({ status: 'DELETING'})
    axios({
      method: 'DELETE',
      url: `${process.env.REACT_APP_API_BASE_URL}/services/${serviceTag}`,
      headers: { 'content-type': 'application/json' },
    }).then(() => {
      setServiceDeleting({ status: 'DELETED'})
    }).catch(error => {
      console.error('error', error)
    })
  }

  const deleteService = () => {
    Swal.fire({
      title: 'Let me double check!',
      text: `Do you really want to delete ${serviceDetail.Service}?`,
      icon: 'warning',
      confirmButtonText: 'Delete',
      showCloseButton: true,
      buttonsStyling: false,
      allowEnterKey: false,
      customClass: {
        confirmButton: 'hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium bg-red-500'
      }
    }).then(res => {
      const { isConfirmed } = res
      if (isConfirmed) {
        return handleDeleteService()
      }
    })
  }

  const editButton = (<NavLink className="hover:bg-indigo-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium bg-gray-100 block" to={`/services/edit/${serviceTag}`}>
    Edit
  </NavLink>)

  const deleteButton = (<button className="hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium bg-red-500" onClick={deleteService}>
    Delete
  </button>)

  return (
    <>
      {/* Page Header */}
      <PageHeader title={serviceDetail.Service} buttons={[editButton, deleteButton]} />

      {/* Container */}
      <div className="container mt-10 flex">
        <div className="w-1/3">
          <div className="pr-5">
            <div className="text-lg font-bold tracking-normal text-gray-900">Service information</div>
            <div className="text-gray-600 mt-1">This is all the information about the service. You can click through to connected accounts and the parent project.</div>
          </div>
        </div>
        {/* Form in white box */}
        <div className="w-2/3 bg-white rounded-lg box-border shadow-md">
          <DescriptionListItem label="Service name:" value={serviceDetail.Service} />
          <DescriptionListItem label="Project:" value={serviceDetail.Project.Name} url={`/projects/${serviceDetail.Project.Tag}`} />
          <DescriptionListItem label="Tag:" value={serviceDetail.Tag} />
          <DescriptionListItem label="Billing contacts:" value={{ type: "table", icon: poundIcon, items: billingContactstoDisplay.get('contact'), italic: billingContactstoDisplay.get('italic')}} />
          <DescriptionListItem label="Security contacts:" value={{ type: "table", icon: securityIcon, items: securityContactstoDisplay.get('contact'), italic: securityContactstoDisplay.get('italic')}} />
          <DescriptionListItem label="Service contacts:" value={{ type: "table", icon: serviceIcon, items: serviceContactstoDisplay.get('contact'), italic: serviceContactstoDisplay.get('italic')}} />
          <DescriptionListItem label="Accounts:" value={{ type: "table", icon: accountIcon, items: accounts }} />
          <DescriptionListItem label="Project Owner:" value={{ type: "table", icon: securityIcon, items: projectOwners }} />
        </div>
      </div>
    </>
  );
}

export default ServiceRead; 